/**
 * Copied from shadc/ui on 12/13/2024
 * @see {@link https://ui.shadcn.com/docs/components/breadcrumb}
 */

import { cn } from "cn";
import { MoreHorizontalIcon } from "lucide-react";
import { NavLink } from "react-router";

type BreadcrumbProps = React.ComponentProps<"nav"> & {
	separator?: React.ReactNode;
};

export const Breadcrumb: React.FC<BreadcrumbProps> = ({ ...props }) => {
	return <nav aria-label="breadcrumb" {...props} />;
};

export const BreadcrumbList: React.FC<React.ComponentProps<"ol">> = ({
	className,
	...props
}) => {
	return (
		<ol
			className={cn(
				"flex flex-wrap items-center text-sm pl-6 my-4 gap-1.5 wrap-break-word font-medium list-none sm:gap-2.5",
				className,
			)}
			{...props}
		/>
	);
};

export const BreadcrumbItem: React.FC<React.ComponentProps<"li">> = ({
	className,
	...props
}) => {
	return (
		<li
			className={cn(
				"inline-flex items-center gap-1.5 text-content-secondary",
				className,
			)}
			{...props}
		/>
	);
};

type BreadcrumbLinkProps = React.ComponentProps<typeof NavLink>;

export const BreadcrumbLink: React.FC<BreadcrumbLinkProps> = ({
	className,
	...props
}) => {
	return (
		<NavLink
			className={cn(
				"text-content-secondary transition-colors hover:text-content-primary no-underline hover:underline",
				className,
			)}
			{...props}
		/>
	);
};

/**
 * Non-link crumb label. Pass `aria-current="page"` only when it names the
 * current page.
 */
export const BreadcrumbPage: React.FC<React.ComponentProps<"span">> = ({
	className,
	...props
}) => {
	return (
		<span
			className={cn(
				"flex items-center gap-2 text-content-secondary",
				className,
			)}
			{...props}
		/>
	);
};

export const BreadcrumbSeparator: React.FC<
	Omit<React.ComponentProps<"li">, "children">
> = ({ className, ...props }) => {
	return (
		<li
			role="presentation"
			aria-hidden="true"
			className={cn(
				"text-content-secondary [&>svg]:w-3.5 [&>svg]:h-3.5",
				className,
			)}
			{...props}
		>
			/
		</li>
	);
};

export const BreadcrumbEllipsis: React.FC<
	Omit<React.ComponentProps<"span">, "children">
> = ({ className, ...props }) => {
	return (
		<span
			role="presentation"
			aria-hidden="true"
			className={cn("flex size-9 items-center justify-center", className)}
			{...props}
		>
			<MoreHorizontalIcon className="size-4" />
			<span className="sr-only">More</span>
		</span>
	);
};
